import { useState, useMemo } from 'react'
import { format, parseISO, differenceInDays } from 'date-fns'
import { usePantry } from './hooks/usePantry'
import { useShoppingLists } from '../shopping/hooks/useShoppingLists'
import { useShoppingItems } from '../shopping/hooks/useShoppingItems'
import { Spinner } from '../../components/ui/Spinner'
import { Button } from '../../components/ui/Button'
import { cn } from '../../lib/utils'
import { Portal } from '../../components/Portal'

const expiryClass = (dateStr) => {
  if (!dateStr) return ''
  const days = differenceInDays(parseISO(dateStr), new Date())
  if (days <= 7)  return 'text-rose-600 font-semibold'
  if (days <= 30) return 'text-amber-600 font-semibold'
  return 'text-text-muted'
}

const Modal = ({ title, onClose, children }) => (
  <Portal>
  <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
    <div className="absolute inset-0 bg-black/40" />
    <div className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col shadow-card" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border">
        <h2 className="text-base font-bold text-text-primary">{title}</h2>
        <button onClick={onClose} className="text-text-muted p-1">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      </div>
      <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4">{children}</div>
    </div>
  </div>
  </Portal>
)

// ─── Item form ────────────────────────────────────────────────────────────────
const ItemForm = ({ item, locations, onSave, onClose }) => {
  const [name,        setName]        = useState(item?.name        ?? '')
  const [quantity,    setQuantity]    = useState(item?.quantity     ?? '')
  const [unit,        setUnit]        = useState(item?.unit         ?? '')
  const [location,    setLocation]    = useState(item?.location     ?? '')
  const [customLoc,   setCustomLoc]   = useState('')
  const [expiryDate,  setExpiryDate]  = useState(item?.expiry_date ?? '')
  const [minQty,      setMinQty]      = useState(item?.min_quantity ?? '')
  const [saving,      setSaving]      = useState(false)
  const [useCustom,   setUseCustom]   = useState(false)

  const effectiveLocation = useCustom ? customLoc : location

  const submit = async (e) => {
    e.preventDefault()
    if (!name.trim()) return
    setSaving(true)
    try {
      await onSave({
        ...(item ? { id: item.id } : {}),
        name:         name.trim(),
        quantity:     quantity !== '' ? parseFloat(quantity) : null,
        unit:         unit || null,
        location:     effectiveLocation || null,
        expiry_date:  expiryDate || null,
        min_quantity: minQty !== '' ? parseFloat(minQty) : null,
      })
      onClose()
    } finally { setSaving(false) }
  }

  return (
    <Modal title={item ? 'Edit Item' : 'Add Pantry Item'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <input type="text" placeholder="Item name" value={name} onChange={(e) => setName(e.target.value)} autoFocus
          className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-text-muted mb-1 block">Quantity</label>
            <input type="number" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)}
              className="w-full rounded-base border border-border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <div>
            <label className="text-xs text-text-muted mb-1 block">Unit</label>
            <input type="text" placeholder="kg, pcs, ml…" value={unit} onChange={(e) => setUnit(e.target.value)}
              className="w-full rounded-base border border-border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
        </div>

        <div>
          <label className="text-xs text-text-muted mb-1 block">Location</label>
          {useCustom ? (
            <div className="flex gap-2">
              <input type="text" placeholder="New location" value={customLoc} onChange={(e) => setCustomLoc(e.target.value)}
                className="flex-1 rounded-base border border-border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
              <button type="button" onClick={() => setUseCustom(false)} className="text-xs text-text-muted">Back</button>
            </div>
          ) : (
            <div className="flex gap-2">
              <select value={location} onChange={(e) => setLocation(e.target.value)}
                className="flex-1 rounded-base border border-border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary">
                <option value="">No location</option>
                {locations.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              <button type="button" onClick={() => setUseCustom(true)} className="text-xs text-primary font-medium">New</button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-text-muted mb-1 block">Expiry date</label>
            <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)}
              className="w-full rounded-base border border-border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <div>
            <label className="text-xs text-text-muted mb-1 block">Min quantity</label>
            <input type="number" step="any" value={minQty} onChange={(e) => setMinQty(e.target.value)}
              className="w-full rounded-base border border-border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
        </div>

        <div className="flex gap-2">
          <Button type="button" variant="secondary" size="sm" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm" className="flex-1" disabled={saving}>{saving ? <Spinner size="sm" /> : item ? 'Save' : 'Add'}</Button>
        </div>
      </form>
    </Modal>
  )
}

// ─── Add to shopping list modal ───────────────────────────────────────────────
const AddToListModal = ({ item, lists, onClose }) => {
  const [listId, setListId] = useState(lists?.[0]?.id ?? '')
  const { addItem } = useShoppingItems(listId)
  const [adding, setAdding] = useState(false)

  const handle = async () => {
    setAdding(true)
    try {
      await addItem({
        name:     item.name,
        quantity: [item.quantity, item.unit].filter(Boolean).join(' ') || null,
        category: 'Pantry',
      })
      onClose()
    } finally { setAdding(false) }
  }

  return (
    <Modal title="Add to Shopping List" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-text-muted">Add <strong>{item.name}</strong> to a shopping list.</p>
        {lists.length === 0 ? (
          <p className="text-sm text-rose-500">No shopping lists exist yet.</p>
        ) : (
          <select value={listId} onChange={(e) => setListId(e.target.value)}
            className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
            {lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        )}
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="flex-1" onClick={handle} disabled={adding || !listId}>
            {adding ? <Spinner size="sm" /> : 'Add to List'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

// ─── Item card ────────────────────────────────────────────────────────────────
const ItemCard = ({ item, locations, shoppingLists, onUpdate, onDelete }) => {
  const [editing,    setEditing]    = useState(false)
  const [addToList,  setAddToList]  = useState(false)
  const [confirmDel, setConfirmDel] = useState(false)

  return (
    <>
      <div className="bg-surface rounded-xl border border-border p-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-text-primary">{item.name}</span>
            {item.min_quantity != null && (item.quantity ?? 0) <= item.min_quantity && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">Low</span>
            )}
          </div>
          <div className="text-xs text-text-muted mt-0.5 flex items-center gap-2 flex-wrap">
            {(item.quantity != null) && <span>{item.quantity} {item.unit}</span>}
            {item.location && (
              <span className="px-1.5 py-0.5 bg-background rounded-md">{item.location}</span>
            )}
            {item.expiry_date && (
              <span className={expiryClass(item.expiry_date)}>
                Exp {format(parseISO(item.expiry_date), 'MMM d')}
              </span>
            )}
          </div>
        </div>

        <div className="flex gap-1.5 flex-shrink-0">
          <button onClick={() => setAddToList(true)} className="text-text-muted hover:text-primary p-1" title="Add to shopping list">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" />
            </svg>
          </button>
          <button onClick={() => setEditing(true)} className="text-text-muted hover:text-primary p-1">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
          {confirmDel ? (
            <div className="flex gap-1 items-center">
              <button onClick={() => setConfirmDel(false)} className="text-xs text-text-muted">Cancel</button>
              <button onClick={() => onDelete(item.id)} className="text-xs text-rose-500 font-semibold">Del</button>
            </div>
          ) : (
            <button onClick={() => setConfirmDel(true)} className="text-text-muted hover:text-rose-500 p-1">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {editing && (
        <ItemForm item={item} locations={locations} onSave={onUpdate} onClose={() => setEditing(false)} />
      )}
      {addToList && (
        <AddToListModal item={item} lists={shoppingLists} onClose={() => setAddToList(false)} />
      )}
    </>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────
export const PantryPage = () => {
  const { data: items = [], isLoading, addItem, updateItem, deleteItem } = usePantry()
  const { data: shoppingLists = [] } = useShoppingLists()
  const [tab,       setTab]       = useState('all')
  const [location,  setLocation]  = useState('all')
  const [showAdd,   setShowAdd]   = useState(false)

  const locations = useMemo(() => [...new Set(items.map((i) => i.location).filter(Boolean))].sort(), [items])

  const displayItems = useMemo(() => {
    let list = items
    if (tab === 'low') list = list.filter((i) => i.min_quantity != null && (i.quantity ?? 0) <= i.min_quantity)
    if (location !== 'all') list = list.filter((i) => i.location === location)
    return list
  }, [items, tab, location])

  if (isLoading) return <div className="flex justify-center py-16"><Spinner /></div>

  return (
    <div className="px-4 py-5 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-text-primary">Pantry</h1>
          <p className="text-sm text-text-muted mt-0.5">{items.length} item{items.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => setShowAdd(true)}
          className="flex items-center gap-1.5 bg-primary text-white text-sm font-medium px-4 py-2 rounded-xl hover:bg-primary/90">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
          Add Item
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-background rounded-xl p-1">
        {[{ id: 'all', label: 'All' }, { id: 'low', label: `Low Stock (${items.filter((i) => i.min_quantity != null && (i.quantity ?? 0) <= i.min_quantity).length})` }].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={cn('flex-1 py-1.5 text-sm font-medium rounded-lg transition-colors',
              tab === t.id ? 'bg-surface text-primary shadow-sm' : 'text-text-muted')}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Location filter */}
      {locations.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
          {['all', ...locations].map((l) => (
            <button key={l} onClick={() => setLocation(l)}
              className={cn('flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-full capitalize transition-colors',
                location === l ? 'bg-primary text-white' : 'bg-background text-text-muted hover:bg-border')}>
              {l}
            </button>
          ))}
        </div>
      )}

      {displayItems.length === 0 ? (
        <p className="text-sm text-text-muted text-center py-12">
          {tab === 'low' ? 'No low-stock items 🎉' : 'No items found'}
        </p>
      ) : (
        <div className="space-y-2">
          {displayItems.map((item) => (
            <ItemCard
              key={item.id}
              item={item}
              locations={locations}
              shoppingLists={shoppingLists}
              onUpdate={updateItem}
              onDelete={deleteItem}
            />
          ))}
        </div>
      )}

      {showAdd && (
        <ItemForm locations={locations} onSave={addItem} onClose={() => setShowAdd(false)} />
      )}
    </div>
  )
}
